import type {
  RepositoryIntelligenceAnalysisStatus,
  RepositoryIntelligenceAnswer,
  RepositoryIntelligenceFindingSeverity,
  RepositoryIntelligenceQuestionStatus,
  RepositoryIntelligenceRiskLevel,
  RepositoryIntelligenceSync,
} from '@ashniva/types';
import type { Tone } from '@ashniva/ui';

/**
 * The screens' words for Repository Intelligence's codes. A code is one of a fixed list; one this release does not
 * know yet is shown as itself, as a code, never as a sentence somebody else wrote.
 */

/** Why a sync, provisioning or a suspended repository failed (Repository Intelligence's sync error codes). */
const FAILURE_REASONS: Record<string, string> = {
  provider_unreachable: 'The code host could not be reached.',
  provider_rate_limited: 'The code host is limiting requests for now.',
  timeout: 'It took too long and was stopped.',
  disk_quota: 'Repository Intelligence ran out of space for it.',
  credential_invalid: 'The credential for the code host was refused.',
  credential_expired: 'The credential for the code host has expired.',
  repository_not_found: 'The code host no longer has this repository.',
  repository_access_lost: 'Access to this repository was withdrawn at the code host.',
  repository_too_large: 'The repository is larger than Repository Intelligence accepts.',
  history_rewritten: 'The branch history was rewritten, so it is not updated automatically.',
  identity_mismatch: 'The repository at the code host is not the one that was registered.',
  fsck_failed: 'The copy failed its integrity check.',
  policy_refused: 'The repository was refused by Repository Intelligence’s policy.',
  internal: 'Repository Intelligence had an internal error.',
  checkout_missing: 'Repository Intelligence has no copy of it.',
  checkout_replaced: 'Repository Intelligence’s copy was replaced and is being checked.',
  checkout_unverified: 'Repository Intelligence’s copy has not been checked yet.',
};

const QUESTION_FAILURES: Record<string, string> = {
  repository_unavailable: 'The repository was not available when the question was answered.',
  evidence_unavailable: 'The indexed code could not be read.',
  invalid_answer: 'The answer did not pass its checks, so it is not shown.',
  model_unavailable: 'The answering service was not available.',
  model_timeout: 'Answering took too long.',
  budget_exceeded: 'The organization’s usage limit has been reached.',
  timeout: 'Answering took too long.',
  internal: 'Repository Intelligence had an internal error.',
};

const ANALYSIS_FAILURES: Record<string, string> = {
  commit_not_found: 'A commit or branch could not be found in the repository.',
  range_too_large: 'The change is too large to analyse. Try a smaller range.',
  repository_unavailable: 'The repository was not available.',
  index_unavailable: 'The index of the repository was not available.',
  timeout: 'The analysis took too long.',
  internal: 'Repository Intelligence had an internal error.',
  pull_request_not_found: 'The pull request could not be found in this repository.',
  cancelled: 'The analysis was cancelled before it started.',
};

function lookup(table: Record<string, string>, code: string | null | undefined): string | null {
  if (!code) {
    return null;
  }
  return Object.hasOwn(table, code) ? (table[code] ?? null) : `Reason code: ${code}`;
}

export const failureReason = (code: string | null | undefined) => lookup(FAILURE_REASONS, code);
export const questionFailure = (code: string | null | undefined) => lookup(QUESTION_FAILURES, code);
export const analysisFailure = (code: string | null | undefined) => lookup(ANALYSIS_FAILURES, code);

export const SYNC_STATUS_LABELS: Record<RepositoryIntelligenceSync['status'], string> = {
  queued: 'Queued',
  running: 'Running',
  succeeded: 'Updated',
  unchanged: 'No changes',
  failed: 'Failed',
};

export const SYNC_TRIGGER_LABELS: Record<RepositoryIntelligenceSync['trigger'], string> = {
  registration: 'First copy',
  schedule: 'Scheduled',
  manual: 'Sync now',
  event: 'Push event',
};

export const QUESTION_STATUS_LABELS: Record<RepositoryIntelligenceQuestionStatus, string> = {
  queued: 'Waiting to start',
  retrieving: 'Finding evidence in the code',
  generating: 'Putting the answer together',
  validating: 'Checking the answer against the code',
  succeeded: 'Answered',
  insufficient_context: 'Not enough evidence',
  refused: 'Refused',
  budget_exceeded: 'Usage limit reached',
  failed: 'Could not be answered',
};

export const ANALYSIS_STATUS_LABELS: Record<RepositoryIntelligenceAnalysisStatus, string> = {
  queued: 'Waiting to start',
  running: 'Analysing',
  succeeded: 'Done',
  failed: 'Failed',
};

type Confidence = NonNullable<RepositoryIntelligenceAnswer['confidence']>;

/** Always a word as well as a colour: colour alone says nothing to a lot of people. */
export const CONFIDENCE: Record<Confidence, { label: string; tone: Tone }> = {
  high: { label: 'High', tone: 'success' },
  medium: { label: 'Medium', tone: 'info' },
  low: { label: 'Low', tone: 'warning' },
  insufficient: { label: 'Not enough evidence', tone: 'neutral' },
};

export const RISK: Record<RepositoryIntelligenceRiskLevel, { label: string; tone: Tone }> = {
  low: { label: 'Low', tone: 'success' },
  medium: { label: 'Medium', tone: 'warning' },
  high: { label: 'High', tone: 'danger' },
  critical: { label: 'Critical', tone: 'danger' },
};

export const SEVERITY: Record<
  RepositoryIntelligenceFindingSeverity,
  { label: string; tone: Tone }
> = {
  info: { label: 'Info', tone: 'neutral' },
  low: { label: 'Low', tone: 'info' },
  medium: { label: 'Medium', tone: 'warning' },
  high: { label: 'High', tone: 'danger' },
  critical: { label: 'Critical', tone: 'danger' },
};

/** `CONFIRMED`/`INFERRED` on findings and evidence. */
export function evidenceLabel(status: string): string {
  if (status === 'CONFIRMED') return 'Confirmed by the index';
  if (status === 'INFERRED') return 'Inferred from names or paths';
  return 'Unknown';
}

/** "lines 12–20", or "line 12". */
export function linesLabel(start: number, end: number): string {
  return start === end ? `line ${start}` : `lines ${start}–${end}`;
}
